import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import pumaLogo from '../../assets/clientLogos/Puma-logo-black-Transparent-Background.png'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/* Hero visual — multi-region globe + checkout flow */
const PumaHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="puma-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ff6b6b" />
        <stop offset="100%" stopColor="#0066ff" />
      </linearGradient>
      <radialGradient id="puma-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#ff6b6b" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#puma-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* Phone-shaped product card */}
    <g transform="translate(230 230)">
      <rect x="-72" y="-130" width="144" height="260" rx="22" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="-58" y="-110" width="116" height="100" rx="12" fill="url(#puma-grad)" opacity="0.85">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
      </rect>
      <rect x="-58" y="0" width="116" height="8" rx="3" fill="rgba(255,255,255,0.85)" />
      <rect x="-58" y="14" width="80" height="6" rx="3" fill="rgba(255,255,255,0.40)" />
      <rect x="-58" y="36" width="116" height="36" rx="8" fill="rgba(0,212,255,0.85)" />
      <text x="0" y="59" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="14" fill="#fff">ADD TO BAG</text>
      {/* tiny localisations strip */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.55)">
        <text x="-58" y="100">$ £ ¥</text>
      </g>
      <animateTransform attributeName="transform" type="translate" values="230 230;230 222;230 230" dur="6s" repeatCount="indefinite" />
    </g>

    {/* Region tags */}
    <g fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize="12">
      <g><circle cx="80" cy="120" r="22" fill="rgba(255,107,107,0.10)" stroke="rgba(255,107,107,0.55)" /><text x="80" y="125" textAnchor="middle" fill="#ff6b6b">USA</text></g>
      <g><circle cx="380" cy="130" r="22" fill="rgba(0,102,255,0.10)" stroke="rgba(0,102,255,0.55)" /><text x="380" y="135" textAnchor="middle" fill="#0066ff">CA</text></g>
      <g><circle cx="380" cy="340" r="22" fill="rgba(0,212,255,0.10)" stroke="rgba(0,212,255,0.55)" /><text x="380" y="345" textAnchor="middle" fill="#00d4ff">JP</text></g>
    </g>

    {/* checkmark badge */}
    <g>
      <circle cx="80" cy="340" r="22" fill="url(#puma-grad)" />
      <path d="M70 340 l8 8 14 -16" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
)

const pressLinks = [
  {
    href: 'https://www.salesforce.com/ap/customer-success-stories/puma/',
    src: 'Salesforce',
    title: 'PUMA takes a mobile-first approach to ecommerce',
  },
  {
    href: 'https://www.cetdigit.com/customer-success/puma-leverages-the-power-of-salesforce-to-relaunch-its-mobile-sites-and-scale-customer-expectations',
    src: 'CETDIGIT',
    title: "PUMA leverages Salesforce to relaunch its mobile sites and scale customer expectations",
  },
  {
    href: 'https://www.salesforce.com/video/3608030/',
    src: 'Salesforce Live',
    title: 'PUMA drives ecommerce growth across 29 countries with a mobile-first strategy',
  },
]

const Puma = () => (
  <>
    <SEO
      path="/case-studies/puma"
      title="Puma — Global E-commerce Relaunch & First-ever Mobile App"
      description="QA Tech led testing and test automation for Puma's global e-commerce relaunch and the company's first-ever mobile app on Salesforce Commerce Cloud — supporting the marketing team with Sanity Studio component development, Cypress automation, multi-region payment integrations and accessibility."
      keywords="Puma case study, e-commerce launch, Salesforce Commerce Cloud QA, Sanity CMS testing, Cypress automation, Detox React Native, Adyen payment testing, accessibility, marketing team support"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Puma — multi-region e-commerce launch on SFCC',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['Puma', 'Salesforce Commerce Cloud', 'Sanity CMS', 'E-commerce QA'],
        url: 'https://qatechltd.com/case-studies/puma',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>Puma</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <img src={pumaLogo} alt="Puma" className="cs-hero__logo" loading="lazy" />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">Puma</h2>
                <p className="cs-hero__client-sub">Global e-commerce relaunch &mdash; web &amp; first-ever mobile app</p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              First mobile app. Full rebrand. <span className="cs-hero__title-accent">Global launch.</span>
            </h1>

            <p className="cs-hero__lede">
              We led testing and test automation for Puma&rsquo;s biggest
              digital programme to date &mdash; a global e-commerce
              relaunch, the company&rsquo;s <strong>first-ever mobile
              app</strong>, and a full website rebrand on a modernised
              tech stack &mdash; across Europe, the UK, North America,
              South Asia and the Middle East. We built the QA programme
              from scratch and <strong>supported the marketing team with
              Sanity Studio component development</strong>, alongside
              validating payment flows across multiple processors and
              regions.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_puma_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <PumaHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">1st</span><span className="cs-stat__label">mobile app in Puma&rsquo;s history</span></div>
          <div className="cs-stat"><span className="cs-stat__num">100,000+</span><span className="cs-stat__label">test cases executed per week</span></div>
          <div className="cs-stat"><span className="cs-stat__num">8&ndash;12 min</span><span className="cs-stat__label">full suite, executed in parallel</span></div>
          <div className="cs-stat"><span className="cs-stat__num">~45 min</span><span className="cs-stat__label">merge → production for small fixes</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="Puma's biggest digital programme to date.">
          <p>
            A global e-commerce relaunch, a full website rebrand, a
            modernised tech stack &mdash; and the company&rsquo;s
            <strong> first-ever mobile app</strong>. Built on Salesforce
            Commerce Cloud, paired with Sanity Studio CMS, multiple
            payment processors and an internal back-office system. A{' '}
            <strong>27-month engagement (2020 &ndash; 2023)</strong>,
            with delivery across Europe, the UK, North America, South
            Asia (including India), the Middle East and additional Asian
            markets. We led the testing and test automation for the
            entire programme &mdash; web, mobile, and everywhere they
            connect.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="Test strategy" title="Scalable, reusable — 100,000+ test cases run every week.">
          <p>
            We built the testing strategy and test automation capabilities
            from scratch &mdash; and we built them to <em>scale</em>. The
            suite grew into thousands of well-architected, highly
            reusable end-to-end Cypress test cases. Reuse is what made
            the volume sustainable: instead of bloating the codebase, we
            ran the same well-engineered suite many times, in many
            contexts.
          </p>
          <p>
            By steady state, the suite was running roughly{' '}
            <strong>15&ndash;20 times every day</strong> across the
            delivery pipeline &mdash; on every pull-request build, on
            every automatic environment deployment, on every release
            branch. By the end of a typical week the platform had been
            exercised by <strong>100,000+ test cases</strong> &mdash; not
            because we wrote 100,000 tests, but because the same
            well-engineered tests ran consistently across the whole
            development lifecycle.
          </p>
          <p>
            The framework was designed for{' '}
            <strong>independent, parallel execution</strong> from the
            ground up. No shared state, no order dependencies, no
            mystery flakiness from one test polluting another. That meant
            the full suite finished in roughly{' '}
            <strong>8&ndash;12 minutes</strong> regardless of how it
            grew, instead of the hours it would have taken running
            sequentially. Fast feedback wasn&rsquo;t a bonus &mdash; it
            was the architecture.
          </p>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="Sanity Studio &amp; marketing-team support" title="Supporting the CMS the marketing team uses every day.">
          <p>
            An important part of the engagement was working alongside
            Puma&rsquo;s <strong>marketing team</strong> on their{' '}
            <strong>Sanity Studio</strong> CMS &mdash; not leading it,
            but supporting it. We contributed to the development of
            content blocks, schemas, custom inputs and preview surfaces
            with the marketers, then tested it all end-to-end so they
            could launch campaigns on multiple environments without
            engineering hand-holding.
          </p>
          <ul>
            <li>Sanity Studio component development in support of the marketing team</li>
            <li>Custom schemas, content blocks and structured inputs built collaboratively</li>
            <li>Preview &amp; production parity validated end-to-end</li>
            <li>Test data and fixtures built so non-engineers could trial a campaign before it went live</li>
            <li>Direct collaboration with brand and growth on launch readiness</li>
          </ul>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="SFCC + back-office" title="Stock, inventory, sales, customer profiles.">
          <p>
            Validated the <strong>Salesforce Commerce Cloud (SFCC)</strong>
            integration end-to-end &mdash; product catalogue, stock,
            inventory, sales, customer profiles. A small Playwright
            framework covered the back-office system separately so the
            ops team had its own quality bar.
          </p>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="Payments" title="Every journey. Every integration. Every region.">
          <p>
            Payment was where regulators, partners, regions and processors
            all collided. We tested it in flight, end to end &mdash;
            every customer journey on the front, every integration on the
            back, every locale-specific edge case in between.
          </p>
          <p>
            <strong>Payment journeys.</strong> Full checkout flows per
            method: cart through 3D Secure / SCA step-up auth through
            confirmation to order management. Guest checkout, returning
            customers, saved cards, express wallet checkouts (Apple Pay
            / Google Pay / PayPal), failed-payment retries, partial
            refunds, full returns. Each region had its own valid
            combinations &mdash; we covered them all.
          </p>
          <p>
            <strong>Payment integrations.</strong> Validated the back-end
            payment layer too: tokenisation, processor webhooks, async
            payment-status sync, retry-on-timeout, multi-currency
            handling, regional tax calculations and order-state
            propagation. <strong>Adyen</strong> and <strong>Ocapi</strong>
            ran in parallel as the two integrators &mdash; we had to
            keep both sides green.
          </p>
          <ul>
            <li>Visa, Mastercard, Amex &mdash; across 3 regions and currencies</li>
            <li>Apple Pay, Google Pay, PayPal express checkouts</li>
            <li>3D Secure / SCA challenge flows, including step-up authentication</li>
            <li>Adyen &amp; Ocapi integration validation in parallel</li>
            <li>Tokenisation, webhook delivery and async status sync</li>
            <li>Partial &amp; full refunds, order amendments and cancellations</li>
            <li>Failed-payment retries, fallback methods and recovery journeys</li>
            <li>Multi-currency display, conversion and per-region tax handling</li>
          </ul>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="Mobile" title="Puma's first-ever mobile app. We tested it like it was.">
          <p>
            The first native mobile app in Puma&rsquo;s history &mdash;
            built in React Native and shipped globally. Automated with
            Detox, run against real devices and real network conditions
            on every release, paired with manual exploratory testing for
            the bits automation will never catch. Responsive web tested
            in lockstep so the brand felt the same everywhere.
          </p>
          <ul>
            <li>React Native, automated with Detox</li>
            <li>Real-device testing across iOS &amp; Android matrices</li>
            <li>Responsive web parity tested alongside the app</li>
            <li>Manual exploratory testing on every release</li>
          </ul>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="Accessibility &amp; performance" title="WCAG, Section 508 (USA), Lighthouse benchmarks.">
          <p>
            Accessibility tested against both WCAG and Section 508
            standards (US) &mdash; manual and automated, with cypress-axe
            integrated into the framework. Google Lighthouse benchmarks
            were embedded into the automation so UI performance had a
            quality gate, not just a hope.
          </p>
        </SplitSection>

        <SplitSection index={8} align="right" kicker="CI/CD" title="Merge to main, into production — fully autonomous.">
          <p>
            CI/CD pipelines in GitHub Actions made the automated test
            suite a first-class part of the delivery flow. Every pull
            request ran the relevant tests across multiple environments
            before merging.
          </p>
          <p>
            The end-state was an autonomous pipeline from <strong>merge to
            main all the way through to production</strong> &mdash; with a
            <strong> ~45-minute turnaround for small fixes</strong>. No
            manual hand-off, no release theatre, no late-night deploy
            calls. The tests were trusted enough to gate real money.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'Test strategy from scratch',
            'Scalable, reusable test architecture',
            'Parallel test execution (8&ndash;12 min full suite)',
            'UI automation framework (Cypress BDD)',
            'API automation', 'Component unit tests (Jest)',
            'Mobile automation (Detox)', 'Back-office automation (Playwright)',
            'Sanity Studio component support &amp; development',
            'Marketing team enablement',
            'Payment journey testing (cards, wallets, 3DS / SCA)',
            'Payment integration validation (Adyen, Ocapi, webhooks, tokenisation)',
            'Multi-region localisation &amp; currency testing',
            'SFCC integration testing', 'Accessibility (WCAG, Section 508)',
            'Performance (Lighthouse, embedded)', 'CI/CD (GitHub Actions)',
            'UAT &amp; exploratory testing',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'TypeScript', 'JavaScript', 'Next.js', 'React',
            'GraphQL', 'AXIOS',
            'Cypress', 'Cucumber', 'Jest',
            'Detox', 'Playwright',
            'Salesforce Commerce Cloud', 'Sanity',
            'Adyen', 'Ocapi',
            'GitHub Actions', 'Lighthouse', 'cypress-axe',
            'JIRA',
          ]}
        />

        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected coverage of Puma&rsquo;s e-commerce platform on
              Salesforce Commerce Cloud.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_puma_press')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. QA Tech is independent and
              not affiliated with Puma or Salesforce.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Got a multi-region launch coming up?</h2>
          <p>We&rsquo;ve done it before. Across three continents, two devices and a dozen payment methods.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default Puma
